/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { useTheme, isSoftBackground, SoftBackgrounds } from '../utils/index.js';
/**
 * Helper function that returns one of the preset badge color values.
 */
const getBadgeColorValue = (color) => {
    if (!color) {
        return '';
    }
    return isSoftBackground(color) ? SoftBackgrounds[color] : color;
};
/**
 * Helper function that returns one of the preset badge status values.
 */
const getStatusValue = (color) => {
    const statuses = ['positive', 'negative', 'warning', 'informational'];
    return color && statuses.includes(color) ? color : undefined;
};
/**
 * A colorful visual indicator for categorizing items.
 * @example
 * <Badge>Label</Badge>
 * <Badge backgroundColor="sunglow">Label</Badge>
 * <Badge backgroundColor="positive">Label</Badge>
 */
export const Badge = (props) => {
    const { backgroundColor, style, className, children, ...rest } = props;
    useTheme();
    // choosing 'primary' status should result in data-iui-status equaling 'informational'
    const reducedBackgroundColor = backgroundColor === 'primary' ? 'informational' : backgroundColor;
    const statusValue = getStatusValue(reducedBackgroundColor);
    const _style = reducedBackgroundColor && !statusValue
        ? {
            '--iui-badge-background-color': getBadgeColorValue(reducedBackgroundColor),
            ...style,
        }
        : { ...style };
    return (React.createElement("span", { className: cx('iui-badge', className), style: _style, "data-iui-status": statusValue, ...rest }, children));
};
export default Badge;
